import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  Grid,
  Popover,
  Typography,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  InputAdornment,
  Avatar,
  useMediaQuery,
  useTheme,
  Stack,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility,
  VisibilityOff,
  Refresh,
  Print,
  Download,
  LocalShipping as VehicleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday,
  Description,
  EventBusy,
  FactCheck,
  PictureAsPdf as PdfIcon,
  InsertDriveFile as FileIcon,
  Image as ImageIcon,
  CloudDownload as DownloadIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import {
  vehicleList,
  deleteVehicle,
  getVehicleDashboardStats,
  getVehicleDetails,
} from "../../../Services/Services";
import { T } from "../../../theme";
import { useDebounce } from "../../../Hooks/useDebounce";

// ── Reusable Style Fragments ───────────────────────────────────────
const sq = { borderRadius: "4px !important" };

const inputSx = {
  "& .MuiOutlinedInput-root": {
    ...sq,
    bgcolor: T.white,
    transition: "box-shadow 0.2s",
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${T.primaryLight}` },
  },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
};

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.white,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};

const iconBtn = { ...ghostBtn, minWidth: 0, px: 1 };

const dangerBtn = {
  ...sq,
  border: `1.5px solid ${T.redBorder}`,
  bgcolor: T.redBg,
  color: T.red,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": { bgcolor: "#fee2e2", borderColor: T.red, boxShadow: "none" },
};

const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};

const paginationSx = {
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  mt: 1,
  ...sq,
  "& .MuiTablePagination-toolbar": {
    minHeight: 44,
    flexWrap: "wrap",
    gap: 0.5,
    px: 1,
  },
  "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
    fontSize: "0.68rem",
    margin: 0,
    color: T.textSub,
    fontWeight: 500,
  },
  "& .MuiTablePagination-select": {
    fontSize: "0.68rem",
    ...sq,
    border: `1px solid ${T.border}`,
    bgcolor: T.white,
    px: "6px",
  },
  "& .MuiTablePagination-actions button": { color: T.primary },
};

// ── Helpers ────────────────────────────────────────────────────────
const formatDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

// ── Document Categories ────────────────────────────────────────────
const DOCUMENT_CATEGORIES = [
  {
    label: "RC Document",
    icon: "🚗",
    fieldKeys: [
      "rc_document",
      "rc_document_url",
      "rc_document_link",
      "rc_url",
      "rc",
      "registration_certificate",
      "rc_file",
    ],
  },
  {
    label: "Insurance Policy",
    icon: "🛡️",
    fieldKeys: [
      "insurance_document",
      "insurance_document_url",
      "insurance_document_link",
      "insurance_url",
      "insurance",
      "insurance_policy",
      "insurance_file",
    ],
  },
  {
    label: "Pollution Certificate",
    icon: "🌿",
    fieldKeys: [
      "pollution_document",
      "pollution_document_url",
      "pollution_document_link",
      "pollution_url",
      "pollution",
      "pollution_certificate",
      "puc_document",
      "pollution_file",
    ],
  },
  {
    label: "Fitness Certificate",
    icon: "✅",
    fieldKeys: [
      "fitness_certificate",
      "fitness_certificate_url",
      "fitness_certificate_link",
      "fitness_url",
      "fitness",
      "fitness_document",
      "fitness_file",
    ],
  },
  {
    label: "Permit Document",
    icon: "📋",
    fieldKeys: [
      "permit_document",
      "permit_document_url",
      "permit_document_link",
      "permit_url",
      "permit",
      "permit_file",
    ],
  },
  {
    label: "Temporary Registration",
    icon: "📄",
    fieldKeys: [
      "temp_registration",
      "temp_registration_url",
      "temp_registration_link",
      "temporary_registration",
      "temp_reg",
      "temp_reg_file",
    ],
  },
];

const STATUS_CONFIG = {
  active: {
    color: T.green,
    bg: T.greenBg,
    label: "Active",
    icon: <CheckCircleIcon sx={{ fontSize: 12 }} />,
  },
  expiring_soon: {
    color: T.amber,
    bg: T.amberBg,
    label: "Expiring Soon",
    icon: <WarningIcon sx={{ fontSize: 12 }} />,
  },
  expired: {
    color: T.red,
    bg: T.redBg,
    label: "Expired",
    icon: <ErrorIcon sx={{ fontSize: 12 }} />,
  },
};

// ── URL Helpers ────────────────────────────────────────────────────
const resolveUrl = (raw) => {
  if (!raw) return null;
  const s = String(raw).trim();
  if (!s) return null;
  if (s.startsWith("http://") || s.startsWith("https://")) return s;
  if (s.startsWith("//")) return window.location.protocol + s;
  const base = (
    process.env.REACT_APP_API_URL || "http://localhost:8000"
  ).replace(/\/$/, "");
  let path = s.startsWith("/") ? s : "/" + s;
  if (!path.startsWith("/media/")) path = "/media" + path;
  return base + path;
};

const isImageUrl = (url) =>
  url ? /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?.*)?$/i.test(url) : false;

const extractDocuments = (data) => {
  const docs = [];
  if (!data) return docs;
  DOCUMENT_CATEGORIES.forEach(({ label, icon, fieldKeys }) => {
    let raw = null;
    for (const key of fieldKeys) {
      if (data[key]) {
        raw = data[key];
        break;
      }
    }
    if (!raw && data.documents && typeof data.documents === "object") {
      for (const key of fieldKeys) {
        if (data.documents[key]) {
          raw = data.documents[key];
          break;
        }
      }
    }
    if (!raw) return;
    const url = resolveUrl(raw);
    if (!url) return;
    docs.push({
      label,
      icon,
      url,
      originalPath: raw,
      isImage: isImageUrl(url),
    });
  });
  return docs;
};

// ── ActionButton ───────────────────────────────────────────────────
const ActionButton = ({
  tip,
  icon,
  isDelete = false,
  isEdit = false,
  isView = false,
  isPurple = false,
  onClick,
}) => {
  const color = isDelete
    ? "#dc2626"
    : isEdit
      ? "#757575"
      : isView
        ? "#1976d2"
        : isPurple
          ? T.purple
          : T.primary;

  const bg = isDelete
    ? T.redBg
    : isEdit
      ? "#f5f5f5"
      : isView
        ? "#e3f2fd"
        : isPurple
          ? T.purpleBg
          : T.primaryBg;

  const border = isDelete
    ? T.redBorder
    : isEdit
      ? "#e0e0e0"
      : isView
        ? "#bbdefb"
        : isPurple
          ? "rgba(139,92,246,0.2)"
          : T.border;

  const hoverBg = isDelete
    ? "#fee2e2"
    : isEdit
      ? "#eeeeee"
      : isView
        ? "#bbdefb"
        : isPurple
          ? "rgba(139,92,246,0.15)"
          : T.primaryLight;

  return (
    <Tooltip title={tip} arrow placement="top">
      <IconButton
        size="small"
        onClick={onClick}
        sx={{
          width: 20,
          height: 20,
          ...sq,
          color,
          bgcolor: bg,
          border: `1px solid ${border}`,
          transition: "all 0.18s",
          "&:hover": {
            bgcolor: hoverBg,
            borderColor: color,
            transform: "translateY(-1px)",
          },
        }}
      >
        {React.cloneElement(icon, { sx: { fontSize: 12 } })}
      </IconButton>
    </Tooltip>
  );
};

// ── DialogHeader ───────────────────────────────────────────────────
const DialogHeader = ({ title, subtitle, onClose, extra }) => (
  <Box
    sx={{
      background: `linear-gradient(135deg, ${T.primary} 0%, ${T.primaryMid} 100%)`,
      px: 2.5,
      py: 1.75,
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    }}
  >
    <Box>
      <Typography
        sx={{
          fontWeight: 700,
          color: T.white,
          fontSize: "0.88rem",
          letterSpacing: "0.01em",
        }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography
          sx={{ color: "rgba(255,255,255,0.75)", fontSize: "0.7rem" }}
        >
          {subtitle}
        </Typography>
      )}
    </Box>
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
      {extra}
      <IconButton
        onClick={onClose}
        size="small"
        sx={{
          color: "rgba(255,255,255,0.8)",
          p: 0.5,
          ...sq,
          "&:hover": { bgcolor: "rgba(255,255,255,0.15)", color: T.white },
        }}
      >
        <CloseIcon sx={{ fontSize: 17 }} />
      </IconButton>
    </Box>
  </Box>
);

// ── StatCard ───────────────────────────────────────────────────────
const StatCard = ({ title, value, icon: Icon, color }) => (
  <Paper
    elevation={0}
    sx={{
      p: 1.5,
      ...sq,
      position: 'relative',
      overflow: 'hidden',
      bgcolor: `${color}12`,
      border: `1px solid ${color}40`,
      boxShadow: 'none',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      transition: 'transform 0.2s, box-shadow 0.2s, border-color 0.2s',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `0 4px 15px ${color}25`,
        borderColor: color,
      },
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: -15,
        right: -15,
        width: 50,
        height: 50,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        zIndex: 0,
      }}
    />
    <Box
      sx={{
        width: 38,
        height: 38,
        borderRadius: '10px',
        bgcolor: `${color}20`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: color,
        zIndex: 1,
      }}
    >
      <Icon sx={{ fontSize: 20 }} />
    </Box>
    <Box sx={{ zIndex: 1, flex: 1 }}>
      <Typography sx={{ fontSize: '1.1rem', fontWeight: 800, color: T.text, lineHeight: 1.2 }}>
        {value || 0}
      </Typography>
      <Typography sx={{ fontSize: '0.65rem', color: T.textSub, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em', mt: 0.2 }}>
        {title}
      </Typography>
    </Box>
  </Paper>
);

// ── ImagePreviewDialog ─────────────────────────────────────────────
const ImagePreviewDialog = ({ open, onClose, imageUrl, title }) => (
  <Dialog
    open={open}
    onClose={onClose}
    maxWidth="lg"
    fullWidth
    PaperProps={{
      sx: {
        ...sq,
        border: `1.5px solid ${T.border}`,
        boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
        overflow: "hidden",
      },
    }}
  >
    <DialogHeader title={title} onClose={onClose} />
    <DialogContent
      sx={{
        p: 2,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "60vh",
      }}
    >
      <img
        src={imageUrl}
        alt={title}
        style={{
          maxWidth: "100%",
          maxHeight: "70vh",
          objectFit: "contain",
          borderRadius: 4,
        }}
        onError={(e) => {
          e.target.onerror = null;
          e.target.src =
            "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='1.5'%3E%3Crect x='3' y='3' width='18' height='18' rx='2'/%3E%3Ccircle cx='8.5' cy='8.5' r='1.5'/%3E%3Cpolyline points='21 15 16 10 5 21'/%3E%3C/svg%3E";
        }}
      />
    </DialogContent>
    <DialogActions
      sx={{
        px: 2.5,
        py: 1.5,
        bgcolor: T.white,
        borderTop: `1px solid ${T.border}`,
      }}
    >
      <Button
        size="small"
        startIcon={<OpenInNewIcon />}
        onClick={() => window.open(imageUrl, "_blank")}
        sx={{ ...ghostBtn, height: 32, px: 2 }}
      >
        Open in New Tab
      </Button>
      <Button
        size="small"
        onClick={onClose}
        sx={{ ...ghostBtn, height: 32, px: 2 }}
      >
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

// ── DocumentCard ───────────────────────────────────────────────────
const DocumentCard = ({ doc, onPreview, onDownload, downloadStatus }) => {
  const [thumbError, setThumbError] = useState(false);
  const ext = doc.url.split(".").pop()?.split("?")[0]?.toLowerCase() || "";
  const isPdf = ext === "pdf";

  const Thumb = () => {
    if (doc.isImage && !thumbError) {
      return (
        <Box
          sx={{
            width: 56,
            height: 56,
            ...sq,
            overflow: "hidden",
            border: `1px solid ${T.border}`,
            flexShrink: 0,
          }}
        >
          <img
            src={doc.url}
            alt={doc.label}
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
            onError={() => setThumbError(true)}
          />
        </Box>
      );
    }
    const IconComp = isPdf ? PdfIcon : thumbError ? ImageIcon : FileIcon;
    const iconColor = isPdf ? T.red : T.primary;
    return (
      <Box
        sx={{
          width: 56,
          height: 56,
          ...sq,
          bgcolor: isPdf ? T.redBg : T.primaryBg,
          border: `1px solid ${isPdf ? T.redBorder : T.border}`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <IconComp sx={{ fontSize: 28, color: iconColor }} />
      </Box>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 1.5,
        ...sq,
        border: `1.5px solid ${T.border}`,
        bgcolor: T.white,
        display: "flex",
        flexDirection: "column",
        gap: 1.25,
        height: "100%",
        transition: "background 0.2s, border-color 0.2s",
        "&:hover": { bgcolor: T.primaryLight, borderColor: T.borderHover },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
        <Thumb />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.73rem",
              fontWeight: 700,
              color: T.text,
              lineHeight: 1.3,
              mb: 0.4,
            }}
          >
            {doc.label}
          </Typography>
          <Chip
            label={
              isPdf
                ? "PDF"
                : doc.isImage
                  ? "Image"
                  : ext.toUpperCase() || "File"
            }
            size="small"
            sx={{
              height: 18,
              fontSize: "0.58rem",
              ...sq,
              bgcolor: isPdf ? T.redBg : T.primaryBg,
              color: isPdf ? T.red : T.primary,
              border: `1px solid ${isPdf ? T.redBorder : T.border}`,
              "& .MuiChip-label": { px: 1 },
            }}
          />
        </Box>
      </Box>
      <Stack direction="row" spacing={0.75}>
        <Button
          size="small"
          fullWidth
          startIcon={<Visibility sx={{ fontSize: 13 }} />}
          onClick={() => onPreview(doc)}
          sx={{
            ...ghostBtn,
            height: 28,
            fontSize: "0.65rem",
            color: T.purple,
            borderColor: "rgba(139,92,246,0.3)",
            "&:hover": { bgcolor: T.purpleBg, borderColor: T.purple },
          }}
        >
          View
        </Button>
        <Button
          size="small"
          fullWidth
          startIcon={
            downloadStatus === "downloading" ? (
              <CircularProgress size={12} sx={{ color: T.primary }} />
            ) : downloadStatus === "done" ? (
              <CheckCircleIcon sx={{ fontSize: 13, color: T.green }} />
            ) : (
              <DownloadIcon sx={{ fontSize: 13 }} />
            )
          }
          onClick={() => onDownload(doc)}
          disabled={downloadStatus === "downloading"}
          sx={{
            ...ghostBtn,
            height: 28,
            fontSize: "0.65rem",
            "&.Mui-disabled": { opacity: 0.6 },
          }}
        >
          {downloadStatus === "downloading"
            ? "..."
            : downloadStatus === "done"
              ? "Saved"
              : "Download"}
        </Button>
      </Stack>
    </Paper>
  );
};

// ── ViewDocumentsDialog ────────────────────────────────────────────
const ViewDocumentsDialog = ({ open, onClose, vehicleId, vehicleNumber }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [downloadStatus, setDownloadStatus] = useState({});

  const fetchDocuments = async () => {
    if (!vehicleId) return;
    setLoading(true);
    setError(null);
    setDownloadStatus({});
    try {
      const response = await getVehicleDetails(vehicleId);
      const data = response?.data ?? response;
      const docs = extractDocuments(data);
      setDocuments(docs);
      if (docs.length === 0)
        setError("No documents uploaded for this vehicle.");
    } catch {
      setError("Failed to load documents. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && vehicleId) fetchDocuments();
    if (!open) {
      setDocuments([]);
      setError(null);
      setPreviewOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, vehicleId]);

  const handlePreview = (doc) => {
    if (doc.isImage) {
      setPreviewDoc(doc);
      setPreviewOpen(true);
    } else window.open(doc.url, "_blank");
  };

  const handleDownload = async (doc) => {
    setDownloadStatus((p) => ({ ...p, [doc.label]: "downloading" }));
    try {
      const response = await fetch(doc.url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const ext =
        doc.url.split(".").pop()?.split("?")[0]?.toLowerCase() || "bin";
      const filename = `${doc.label.replace(/\s+/g, "_")}_${vehicleNumber || "vehicle"}.${ext}`;
      const blobUrl = window.URL.createObjectURL(blob);
      const a = Object.assign(document.createElement("a"), {
        href: blobUrl,
        download: filename,
        style: "display:none",
      });
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        window.URL.revokeObjectURL(blobUrl);
      }, 100);
      setDownloadStatus((p) => ({ ...p, [doc.label]: "done" }));
      setTimeout(
        () => setDownloadStatus((p) => ({ ...p, [doc.label]: "idle" })),
        3000,
      );
    } catch {
      window.open(doc.url, "_blank");
      setDownloadStatus((p) => ({ ...p, [doc.label]: "idle" }));
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            ...sq,
            border: `1.5px solid ${T.border}`,
            boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
            overflow: "hidden",
          },
        }}
      >
        <DialogHeader
          title="Vehicle Documents"
          subtitle={vehicleNumber}
          onClose={onClose}
          extra={
            <Tooltip title="Refresh">
              <IconButton
                size="small"
                onClick={fetchDocuments}
                sx={{
                  color: "rgba(255,255,255,0.8)",
                  p: 0.5,
                  ...sq,
                  "&:hover": { color: T.white },
                }}
              >
                <Refresh sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
          }
        />
        <DialogContent
          sx={{
            p: 2,
            minHeight: 280,
            maxHeight: "70vh",
            overflow: "auto",
            bgcolor: T.white,
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                height: 200,
                gap: 2,
              }}
            >
              <CircularProgress sx={{ color: T.primary }} size={36} />
              <Typography
                sx={{ fontSize: "0.75rem", color: T.textSub, fontWeight: 500 }}
              >
                Loading documents…
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Description sx={{ fontSize: 48, color: T.border, mb: 1 }} />
              <Typography
                sx={{
                  color: T.red,
                  fontSize: "0.8rem",
                  mb: 1.5,
                  fontWeight: 500,
                }}
              >
                {error}
              </Typography>
              <Button
                size="small"
                onClick={fetchDocuments}
                sx={{ ...ghostBtn, height: 30, px: 2 }}
              >
                Retry
              </Button>
            </Box>
          ) : (
            <Grid container spacing={1.5}>
              {documents.map((doc, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <DocumentCard
                    doc={doc}
                    onPreview={handlePreview}
                    onDownload={handleDownload}
                    downloadStatus={downloadStatus[doc.label] || "idle"}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </DialogContent>
        {!loading && !error && documents.length > 0 && (
          <DialogActions
            sx={{
              px: 2.5,
              py: 1.5,
              bgcolor: T.white,
              borderTop: `1px solid ${T.border}`,
              justifyContent: "space-between",
            }}
          >
            <Typography
              sx={{ fontSize: "0.65rem", color: T.textMuted, fontWeight: 500 }}
            >
              {documents.length} document{documents.length !== 1 ? "s" : ""}{" "}
              found
            </Typography>
            <Button
              size="small"
              onClick={onClose}
              sx={{ ...ghostBtn, height: 32, px: 2 }}
            >
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>
      <ImagePreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        imageUrl={previewDoc?.url || ""}
        title={`${previewDoc?.label || ""} — ${vehicleNumber || ""}`}
      />
    </>
  );
};

// ── InfoRow ────────────────────────────────────────────────────────
const InfoRow = ({ Icon, label, val, highlight }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
    <Box
      sx={{
        width: 24,
        height: 24,
        ...sq,
        bgcolor: T.white,
        border: `1px solid ${T.border}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <Icon sx={{ fontSize: 13, color: T.primary }} />
    </Box>
    <Box>
      <Typography
        sx={{
          fontSize: "0.62rem",
          color: T.textMuted,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.75rem",
          color: highlight ? T.primary : T.text,
          fontWeight: 500,
          lineHeight: 1.3,
        }}
      >
        {val || "—"}
      </Typography>
    </Box>
  </Box>
);

// ── MobileVehicleCard ──────────────────────────────────────────────
const MobileVehicleCard = ({
  vehicle,
  expanded,
  onToggle,
  onEdit,
  onDelete,
  onViewDocuments,
}) => {
  const statusConfig = STATUS_CONFIG[vehicle?.status] || STATUS_CONFIG.active;
  return (
    <Paper
      elevation={0}
      sx={{
        mb: 1,
        ...sq,
        bgcolor: T.white,
        overflow: "hidden",
        border: `1.5px solid ${expanded ? T.primary : T.border}`,
        boxShadow: expanded ? "0 2px 12px rgba(79,70,229,0.1)" : "none",
        transition: "border-color 0.2s, box-shadow 0.2s",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "stretch", minHeight: 64 }}>
        {/* Icon block */}
        <Box
          sx={{
            width: 60,
            bgcolor: expanded ? T.primary : T.primaryBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRight: `1.5px solid ${expanded ? T.primary : T.border}`,
            transition: "background-color 0.2s",
            flexShrink: 0,
          }}
        >
          <VehicleIcon
            sx={{ fontSize: 28, color: expanded ? T.white : T.primary }}
          />
        </Box>
        {/* Info */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            px: 1.5,
            py: 1,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: "0.82rem",
              color: T.text,
              lineHeight: 1.2,
              mb: 0.3,
            }}
          >
            {vehicle.vehicle_number || "—"}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.65rem",
              color: T.textMuted,
              fontWeight: 600,
              fontFamily: "monospace",
            }}
          >
            {vehicle.chassis_number || "—"}
          </Typography>
          <Box sx={{ mt: 0.4 }}>
            <Chip
              icon={statusConfig.icon}
              label={statusConfig.label}
              size="small"
              sx={{
                fontSize: "0.55rem",
                height: 18,
                ...sq,
                bgcolor: statusConfig.bg,
                color: statusConfig.color,
                border: `1px solid ${statusConfig.color}33`,
                "& .MuiChip-icon": { color: statusConfig.color, ml: 0.5 },
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          </Box>
        </Box>
        {/* Toggle */}
        <Box sx={{ display: "flex", alignItems: "center", pr: 1.5 }}>
          <Button
            size="small"
            onClick={onToggle}
            startIcon={
              expanded ? (
                <VisibilityOff sx={{ fontSize: 12 }} />
              ) : (
                <Visibility sx={{ fontSize: 12 }} />
              )
            }
            sx={{
              ...ghostBtn,
              height: 28,
              fontSize: "0.62rem",
              px: 1,
              "& .MuiButton-startIcon": { mr: "3px", ml: 0 },
            }}
          >
            {expanded ? "Hide" : "Details"}
          </Button>
        </Box>
      </Box>

      {expanded && (
        <Box
          sx={{
            bgcolor: T.white,
            borderTop: `1.5px solid ${T.border}`,
            p: 1.5,
          }}
        >
          <Stack spacing={1}>
            <InfoRow
              Icon={Description}
              label="Chassis"
              val={vehicle.chassis_number}
            />
            <InfoRow
              Icon={CalendarToday}
              label="Insurance Expiry"
              val={formatDate(vehicle.insurance_expiry)}
            />
            <InfoRow
              Icon={EventBusy}
              label="Pollution Expiry"
              val={formatDate(vehicle.pollution_expiry)}
            />
            <InfoRow
              Icon={FactCheck}
              label="Fitness Expiry"
              val={formatDate(vehicle.fitness_validity)}
            />
            {vehicle.notes && (
              <InfoRow Icon={Description} label="Notes" val={vehicle.notes} />
            )}
          </Stack>
          <Box
            sx={{
              display: "flex",
              gap: 0.75,
              mt: 1.5,
              pt: 1.25,
              borderTop: `1px dashed ${T.border}`,
              justifyContent: "flex-end",
            }}
          >
            <Button
              size="small"
              startIcon={<Description sx={{ fontSize: 13 }} />}
              onClick={() => onViewDocuments(vehicle)}
              sx={{
                ...ghostBtn,
                height: 30,
                px: 1.5,
                color: T.purple,
                borderColor: "rgba(139,92,246,0.3)",
                "&:hover": { bgcolor: T.purpleBg, borderColor: T.purple },
              }}
            >
              Docs
            </Button>
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 13 }} />}
              onClick={() => onEdit(vehicle)}
              sx={{ ...ghostBtn, height: 30, px: 1.5 }}
            >
              Edit
            </Button>
            <Button
              size="small"
              startIcon={<DeleteIcon sx={{ fontSize: 13 }} />}
              onClick={(e) => onDelete(e, vehicle.vehicle_id || vehicle.id)}
              sx={{ ...dangerBtn, height: 30, px: 1.5 }}
            >
              Delete
            </Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
};

// ── DesktopVehicleTable ────────────────────────────────────────────
const DesktopVehicleTable = ({
  vehicles,
  loading,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onView,
  onEdit,
  onDelete,
  onViewDocuments,
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          py: 10,
          gap: 2,
        }}
      >
        <CircularProgress sx={{ color: T.primary }} size={36} />
        <Typography
          sx={{ fontSize: "0.75rem", color: T.textSub, fontWeight: 500 }}
        >
          Loading vehicles…
        </Typography>
      </Box>
    );
  }

  const columns = [
    { label: "#", align: "center", sticky: true, left: 0, minW: 48 },
    { label: "Vehicle No.", align: "left", sticky: true, left: 48, minW: 140 },
    { label: "Chassis No.", align: "center", minW: 150 },
    { label: "Insurance Expiry", align: "center", minW: 120 },
    { label: "Pollution Expiry", align: "center", minW: 120 },
    { label: "Fitness Expiry", align: "center", minW: 120 },
    { label: "Status", align: "center", minW: 110 },
    { label: "Actions", align: "center", sticky: true, right: 0, minW: 130 },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        ...sq,
        border: `1.5px solid ${T.border}`,
        overflow: "hidden",
        boxShadow: "0 1px 8px rgba(79,70,229,0.06)",
      }}
    >
      <TableContainer
        sx={{ maxHeight: "calc(100vh - 340px)", overflow: "auto" }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: 860,
            borderCollapse: "collapse",
            "& td, & th": {
              py: 0,
              px: 1,
              fontSize: "0.75rem",
              borderBottom: `1px solid #ccc`,
              lineHeight: 1.2,
            },
            "& th": {
              fontWeight: 700,
              color: T.headerText,
              bgcolor: `${T.headerBg} !important`,
              py: 0.2,
              fontSize: "0.8rem",
              letterSpacing: "0.01em",
              borderBottom: `1px solid #ccc`,
              lineHeight: 1.2,
              height: 28,
            },
            "& td": {
              height: 26,
            },
          }}
        >
          <TableHead>
            <TableRow>
              {columns.map(({ label, align, sticky, left, right, minW }) => (
                <TableCell
                  key={label}
                  align={align}
                  sx={{
                    minWidth: minW,
                    ...(sticky && {
                      position: "sticky",
                      ...(left !== undefined && { left }),
                      ...(right !== undefined && { right }),
                      zIndex: 12,
                      bgcolor: `${T.headerBg} !important`,
                      color: `${T.headerText} !important`,
                    }),
                  }}
                >
                  {label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {vehicles.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={8}
                  sx={{ textAlign: "center", py: 6, border: "none" }}
                >
                  <VehicleIcon
                    sx={{
                      fontSize: 40,
                      color: T.border,
                      mb: 1,
                      display: "block",
                      mx: "auto",
                    }}
                  />
                  <Typography
                    sx={{
                      color: T.textSub,
                      fontSize: "0.8rem",
                      fontWeight: 600,
                    }}
                  >
                    No vehicles found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              vehicles.map((vehicle, idx) => {
                const isEven = idx % 2 === 0;
                const rowBg = isEven ? T.baseRow : T.altRow;
                const statusConfig =
                  STATUS_CONFIG[vehicle?.status] || STATUS_CONFIG.active;
                const id = vehicle.vehicle_id || vehicle.id;

                return (
                  <TableRow
                    key={id}
                    sx={{
                      "& > td": {
                        bgcolor: rowBg,
                        transition: "background-color 0.12s",
                      },
                      "&:hover > td": {
                        bgcolor: `${T.rowHover} !important`,
                      },
                    }}
                  >
                    {/* S.No */}
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        left: 0,
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          fontWeight: 700,
                          color: T.textMuted,
                        }}
                      >
                        {page * rowsPerPage + idx + 1}
                      </Typography>
                    </TableCell>

                    {/* Vehicle No. */}
                    <TableCell
                      align="left"
                      sx={{
                        position: "sticky",
                        left: 48,
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                        py: "2px !important",
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          sx={{
                            width: 20,
                            height: 20,
                            ...sq,
                            bgcolor: T.primaryLight,
                            color: T.primary,
                            fontSize: "0.55rem",
                            fontWeight: 800,
                            border: `1px solid ${T.border}`,
                            flexShrink: 0,
                          }}
                        >
                          <VehicleIcon sx={{ fontSize: 11 }} />
                        </Avatar>
                        <Typography
                          sx={{
                            fontSize: "0.75rem",
                            fontWeight: 700,
                            color: T.text,
                          }}
                        >
                          {vehicle.vehicle_number || "—"}
                        </Typography>
                      </Stack>
                    </TableCell>

                    {/* Chassis No. */}
                    <TableCell align="center">
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: T.text,
                          fontWeight: 500,
                          fontFamily: "monospace",
                        }}
                      >
                        {vehicle.chassis_number || "—"}
                      </Typography>
                    </TableCell>

                    {/* Insurance Expiry */}
                    <TableCell align="center">
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: T.text,
                          fontWeight: 500,
                        }}
                      >
                        {formatDate(vehicle.insurance_expiry)}
                      </Typography>
                    </TableCell>

                    {/* Pollution Expiry */}
                    <TableCell align="center">
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: T.text,
                          fontWeight: 500,
                        }}
                      >
                        {formatDate(vehicle.pollution_expiry)}
                      </Typography>
                    </TableCell>

                    {/* Fitness Expiry */}
                    <TableCell align="center">
                      <Typography
                        sx={{
                          fontSize: "0.75rem",
                          color: T.text,
                          fontWeight: 500,
                        }}
                      >
                        {formatDate(vehicle.fitness_validity)}
                      </Typography>
                    </TableCell>

                    {/* Status */}
                    <TableCell align="center">
                      <Chip
                        icon={statusConfig.icon}
                        label={statusConfig.label}
                        size="small"
                        sx={{
                          fontSize: "0.6rem",
                          height: 16,
                          ...sq,
                          bgcolor: statusConfig.bg,
                          color: statusConfig.color,
                          border: `1px solid ${statusConfig.color}33`,
                          "& .MuiChip-icon": { color: statusConfig.color },
                          "& .MuiChip-label": { px: 0.75 },
                        }}
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        right: 0,
                        bgcolor: `${rowBg} !important`,
                        zIndex: 5,
                        py: "2px !important",
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="center"
                      >
                        <ActionButton
                          tip="Documents"
                          icon={<Description />}
                          isPurple
                          onClick={() => onViewDocuments(vehicle)}
                        />
                        <ActionButton
                          tip="View"
                          icon={<Visibility />}
                          isView
                          onClick={() => onView(vehicle)}
                        />
                        <ActionButton
                          tip="Edit"
                          icon={<EditIcon />}
                          isEdit
                          onClick={() => onEdit(vehicle)}
                        />
                        <ActionButton
                          tip="Delete"
                          icon={<DeleteIcon />}
                          isDelete
                          onClick={(e) => onDelete(e, id)}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {totalCount > 0 && (
        <Box sx={{ borderTop: `1px solid #ccc`, bgcolor: T.white }}>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={onPageChange}
            onRowsPerPageChange={onRowsPerPageChange}
            sx={{
              "& .MuiTablePagination-toolbar": { minHeight: 36, px: 2 },
              "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows":
                {
                  fontSize: "0.7rem",
                  color: T.textSub,
                  fontWeight: 500,
                  margin: 0,
                },
              "& .MuiTablePagination-select": {
                fontSize: "0.7rem",
                ...sq,
                border: `1px solid #ccc`,
                bgcolor: T.white,
                px: "5px",
              },
              "& .MuiTablePagination-actions button": { color: T.primary },
              "& .MuiTablePagination-actions button:disabled": {
                color: T.textMuted,
              },
            }}
          />
        </Box>
      )}
    </Paper>
  );
};

// ── ViewVehicleDialog ──────────────────────────────────────────────
const ViewVehicleDialog = ({ open, onClose, vehicle }) => {
  if (!vehicle) return null;
  const statusConfig = STATUS_CONFIG[vehicle?.status] || STATUS_CONFIG.active;
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          ...sq,
          border: `1.5px solid ${T.border}`,
          boxShadow: "0 8px 32px rgba(79,70,229,0.15)",
          overflow: "hidden",
        },
      }}
    >
      <DialogHeader title="Vehicle Details" onClose={onClose} />
      <DialogContent sx={{ p: 2.5, bgcolor: T.white }}>
        <Stack alignItems="center" sx={{ mb: 2.5 }}>
          <Avatar
            sx={{
              width: 64,
              height: 64,
              ...sq,
              background: `linear-gradient(135deg, ${T.primaryLight}, ${T.primaryBg})`,
              color: T.primary,
              border: `2px solid ${T.border}`,
              mb: 1,
            }}
          >
            <VehicleIcon sx={{ fontSize: 32 }} />
          </Avatar>
          <Typography sx={{ fontWeight: 800, color: T.text, fontSize: "1rem" }}>
            {vehicle.vehicle_number}
          </Typography>
          <Chip
            icon={statusConfig.icon}
            label={statusConfig.label}
            size="small"
            sx={{
              mt: 0.5,
              ...sq,
              bgcolor: statusConfig.bg,
              color: statusConfig.color,
              border: `1px solid ${statusConfig.color}33`,
              fontSize: "0.62rem",
              height: 20,
            }}
          />
        </Stack>
        <Box
          sx={{
            p: 2,
            bgcolor: T.white,
            border: `1.5px solid ${T.border}`,
            ...sq,
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: T.primary,
              mb: 1.25,
              fontSize: "0.68rem",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            Vehicle Information
          </Typography>
          <Stack spacing={1.25}>
            <InfoRow
              Icon={Description}
              label="Chassis Number"
              val={vehicle.chassis_number}
            />
            <InfoRow
              Icon={CalendarToday}
              label="Insurance Expiry"
              val={formatDate(vehicle.insurance_expiry)}
            />
            <InfoRow
              Icon={EventBusy}
              label="Pollution Expiry"
              val={formatDate(vehicle.pollution_expiry)}
            />
            <InfoRow
              Icon={FactCheck}
              label="Fitness Expiry"
              val={formatDate(vehicle.fitness_validity)}
            />
            {vehicle.notes && (
              <InfoRow Icon={Description} label="Notes" val={vehicle.notes} />
            )}
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          bgcolor: T.white,
          borderTop: `1px solid ${T.border}`,
        }}
      >
        <Button
          onClick={onClose}
          size="small"
          sx={{ ...ghostBtn, height: 32, px: 2 }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── DeletePopover ──────────────────────────────────────────────────
const DeletePopover = ({ anchorEl, onClose, onConfirm }) => (
  <Popover
    open={Boolean(anchorEl)}
    anchorEl={anchorEl}
    onClose={onClose}
    anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
    transformOrigin={{ vertical: "top", horizontal: "center" }}
    PaperProps={{
      sx: {
        ...sq,
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        border: `1.5px solid ${T.border}`,
        bgcolor: T.white,
      },
    }}
  >
    <Box sx={{ p: 2, maxWidth: 240 }}>
      <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            ...sq,
            bgcolor: T.redBg,
            border: `1.5px solid ${T.redBorder}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <DeleteIcon sx={{ fontSize: 20, color: T.red }} />
        </Box>
      </Box>
      <Typography
        sx={{
          fontWeight: 700,
          fontSize: "0.78rem",
          color: T.text,
          textAlign: "center",
          mb: 0.5,
        }}
      >
        Delete Vehicle?
      </Typography>
      <Typography
        sx={{
          fontSize: "0.67rem",
          color: T.textSub,
          fontWeight: 500,
          textAlign: "center",
          mb: 1.75,
        }}
      >
        This action cannot be undone.
      </Typography>
      <Box sx={{ display: "flex", gap: 0.75 }}>
        <Button
          fullWidth
          size="small"
          onClick={onClose}
          sx={{ ...ghostBtn, height: 30, fontSize: "0.7rem" }}
        >
          Cancel
        </Button>
        <Button
          fullWidth
          size="small"
          onClick={onConfirm}
          sx={{ ...dangerBtn, height: 30, fontSize: "0.7rem" }}
        >
          Delete
        </Button>
      </Box>
    </Box>
  </Popover>
);

// ── Main Component ─────────────────────────────────────────────────
export default function VehicleList() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [vehicles, setVehicles] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [deleteAnchorEl, setDeleteAnchorEl] = useState(null);
  const [vehicleToDelete, setVehicleToDelete] = useState(null);
  const [documentsDialogOpen, setDocumentsDialogOpen] = useState(false);
  const [selectedVehicleForDocs, setSelectedVehicleForDocs] = useState(null);
  const [stats, setStats] = useState({
    total_vehicles: 0,
    active_vehicles: 0,
    expiring_soon: 0,
    expired: 0,
  });
  const [expandedCardId, setExpandedCardId] = useState(null);

  const handleCardToggle = (id) =>
    setExpandedCardId((p) => (p === id ? null : id));
  const debouncedSearch = useDebounce(searchTerm, 500);

  const fetchVehicles = useCallback(async () => {
    setLoading(true);
    try {
      const response = await vehicleList(debouncedSearch, "", page + 1); 
      if (response?.data) {
        setVehicles(response.data.results || []);
        setTotalCount(response.data.count || 0);
      } else if (response?.results) {
        setVehicles(response.results || []);
        setTotalCount(response.count || 0);
      } else {
        setVehicles([]);
        setTotalCount(0);
      }
    } catch (e) {
      console.error("Error fetching vehicles:", e);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, page]); 

  const fetchStats = useCallback(async () => {
    try {
      const response = await getVehicleDashboardStats();
      if (response) setStats(response);
    } catch (e) {
      console.error("Error fetching stats:", e);
    }
  }, []);

  useEffect(() => {
    fetchVehicles();
    fetchStats();
  }, [fetchVehicles, fetchStats]);

 useEffect(() => {
    const q = debouncedSearch.toLowerCase(); // ← changed
    const filtered = vehicles.filter(
      (v) =>
        !q ||
        v.vehicle_number?.toLowerCase().includes(q) ||
        v.chassis_number?.toLowerCase().includes(q),
    );
    setFilteredVehicles(filtered);
    setTotalCount(filtered.length);
    setPage(0);
    setExpandedCardId(null);
  }, [vehicles, debouncedSearch]);

  const handleView = (vehicle) => {
    setSelectedVehicle(vehicle);
    setViewOpen(true);
  };
  const handleEdit = (vehicle) =>
    navigate(`/dashboard/VehicleFrom/${vehicle.vehicle_id || vehicle.id}`);
  const handleViewDocuments = (vehicle) => {
    setSelectedVehicleForDocs(vehicle);
    setDocumentsDialogOpen(true);
  };
  const handleDeleteClick = (e, id) => {
    setDeleteAnchorEl(e.currentTarget);
    setVehicleToDelete(id);
  };
  const handleDeleteClose = () => {
    setDeleteAnchorEl(null);
    setVehicleToDelete(null);
  };
  const handleDeleteConfirm = async () => {
    if (!vehicleToDelete) return;
    try {
      await deleteVehicle(vehicleToDelete);
      fetchVehicles();
      fetchStats();
    } catch (e) {
      console.error("Error deleting vehicle:", e);
    }
    handleDeleteClose();
  };

  const handlePrint = () => {
    const w = window.open("", "_blank");
    w.document.write(`
      <html><head><title>Vehicle List</title>
      <style>* { margin:0; padding:0; box-sizing:border-box; } body { font-family:'Segoe UI',Arial,sans-serif; padding:30px; color:#1e1b4b; }
      h2 { text-align:center; font-size:18px; margin-bottom:20px; color:#4f46e5; }
      table { width:100%; border-collapse:collapse; font-size:12px; }
      th { background:#4f46e5; color:white; padding:8px 10px; text-align:left; font-weight:600; }
      td { padding:7px 10px; border-bottom:1px solid #e0e7ff; }
      tr:nth-child(even) td { background:#f5f3ff; }</style></head>
      <body><h2>Vehicle List</h2>
      <table><thead><tr><th>#</th><th>Vehicle No.</th><th>Chassis No.</th><th>Insurance Expiry</th><th>Pollution Expiry</th><th>Status</th></tr></thead>
      <tbody>${filteredVehicles.map((v, i) => `<tr><td>${i + 1}</td><td>${v.vehicle_number || "—"}</td><td>${v.chassis_number || "—"}</td><td>${formatDate(v.insurance_expiry)}</td><td>${formatDate(v.pollution_expiry)}</td><td>${STATUS_CONFIG[v?.status]?.label || v?.status || "—"}</td></tr>`).join("")}
      </tbody></table></body></html>`);
    w.document.close();
    w.print();
    w.close();
  };

const handleExportExcel = async () => {
  const XLSX = await import("xlsx-js-style");

  const headers = [
    "Sr No",
    "Vehicle Number",
    "Chassis Number",
    "Insurance Expiry",
    "Pollution Expiry",
    "Fitness Validity",
    "Status",
  ];

  const rows = filteredVehicles.map((v, i) => [
    i + 1,
    v.vehicle_number || "—",
    v.chassis_number || "—",
    formatDate(v.insurance_expiry),
    formatDate(v.pollution_expiry),
    formatDate(v.fitness_validity),
    STATUS_CONFIG[v?.status]?.label || v?.status || "—",
  ]);

  // ── Style Definitions ──────────────────────────────────────────
  const headerStyle = {
    font: { bold: true, color: { rgb: "FFFFFF" }, sz: 10, name: "Segoe UI" },
    fill: { fgColor: { rgb: "4F46E5" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: false },
    border: {
      top:    { style: "thin", color: { rgb: "3730A3" } },
      bottom: { style: "thin", color: { rgb: "3730A3" } },
      left:   { style: "thin", color: { rgb: "3730A3" } },
      right:  { style: "thin", color: { rgb: "3730A3" } },
    },
  };

  const cellStyleEven = {
    font: { sz: 10, name: "Segoe UI", color: { rgb: "1E1B4B" } },
    fill: { fgColor: { rgb: "F5F3FF" } },
    alignment: { horizontal: "center", vertical: "center", wrapText: false },
    border: {
      top:    { style: "thin", color: { rgb: "E0E7FF" } },
      bottom: { style: "thin", color: { rgb: "E0E7FF" } },
      left:   { style: "thin", color: { rgb: "E0E7FF" } },
      right:  { style: "thin", color: { rgb: "E0E7FF" } },
    },
  };

  const cellStyleOdd = {
    ...cellStyleEven,
    fill: { fgColor: { rgb: "FFFFFF" } },
  };

  const numberStyle = {
    ...cellStyleEven,
    font: { ...cellStyleEven.font, bold: true, color: { rgb: "4F46E5" } },
  };

  const numberStyleOdd = {
    ...numberStyle,
    fill: { fgColor: { rgb: "FFFFFF" } },
  };

  // ── Build Worksheet Data ───────────────────────────────────────
  const wsData = [
    headers.map((h) => ({ v: h, t: "s", s: headerStyle })),
    ...rows.map((row, rowIdx) =>
      row.map((val, colIdx) => {
        const isEven = rowIdx % 2 === 0;
        const isNumber = colIdx === 0;

        let style;
        if (isNumber) {
          style = isEven ? numberStyle : numberStyleOdd;
        } else {
          style = isEven ? cellStyleEven : cellStyleOdd;
        }

        return {
          v: val ?? "—",
          t: typeof val === "number" ? "n" : "s",
          s: style,
        };
      }),
    ),
  ];

  const ws = XLSX.utils.aoa_to_sheet(wsData);

  // ── Column Widths (no wrapping, width based on longest content) ──
  const colWidths = headers.map((_, colIdx) => {
    const allValues = [
      headers[colIdx],
      ...rows.map((r) => String(r[colIdx] ?? "")),
    ];
    const maxLen = Math.max(...allValues.map((v) => v.length));

    return { wch: maxLen + 4 };
  });

  ws["!cols"] = colWidths;

  // ── Row Heights ────────────────────────────────────────────────
  ws["!rows"] = [{ hpt: 18 }];

  // ── Freeze Header Row ──────────────────────────────────────────
  ws["!freeze"] = { xSplit: 0, ySplit: 1, topLeftCell: "A2" };

  const wb = XLSX.utils.book_new();
  wb.Props = {
    Title: "Vehicle List",
    Author: "Staff Management System",
    CreatedDate: new Date(),
  };

  XLSX.utils.book_append_sheet(wb, ws, "Vehicles");
  XLSX.writeFile(
    wb,
    `Vehicle_List_${new Date().toISOString().split("T")[0]}.xlsx`,
  );
};

  const paginatedVehicles = filteredVehicles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  return (
    <Box sx={{ px: 0, pt: 0, mt: -2, maxWidth: 1400, mx: "auto" }}>
      {/* Stats */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={0.75}>
          {[
            {
              title: "Total Vehicles",
              value: stats.total_vehicles,
              icon: VehicleIcon,
              color:  "#7c3aed",
            },
            {
              title: "Active",
              value: stats.active_vehicles,
              icon: CheckCircleIcon,
              color: "#10b981",
            },
            {
              title: "Expiring Soon",
              value: stats.expiring_soon,
              icon: WarningIcon,
              color: "#d97706",
            },
            {
              title: "Expired",
              value: stats.expired,
              icon: ErrorIcon,
              color: T.red,
            },
          ].map((s) => (
            <Grid item xs={6} sm={3} key={s.title}>
              <StatCard {...s} />
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Toolbar */}
      <Box sx={{ mb: 1.5 }}>
        <Grid container spacing={0.75} alignItems="center">
          <Grid item xs={12} sm={4} md={3.5}>
            <TextField
              fullWidth
              placeholder="Search vehicle or chassis…"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 15, color: T.primary }} />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm("")}
                      sx={{ p: 0.25 }}
                    >
                      <CloseIcon sx={{ fontSize: 13, color: T.textSub }} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiInputBase-root": {
                  height: 34,
                  ...sq,
                  bgcolor: T.white,
                  transition: "box-shadow 0.2s",
                },
                "& fieldset": { border: `1.5px solid ${T.border}` },
                "& :hover fieldset": { border: `1.5px solid ${T.borderHover}` },
                "& .Mui-focused fieldset": {
                  border: `1.5px solid ${T.primary}`,
                },
                "& .Mui-focused": { boxShadow: `0 0 0 3px ${T.primaryLight}` },
              }}
            />
          </Grid>
          <Grid item xs={4} sm={1.5} md={1}>
            <Button
              fullWidth
              size="small"
              onClick={() => setSearchTerm("")}
              sx={{ ...ghostBtn, height: 34 }}
            >
              Reset
            </Button>
          </Grid>
          {[
            {
              tip: "Print",
              icon: <Print sx={{ fontSize: 15 }} />,
              action: handlePrint,
            },
            {
              tip: "Export Excel",
              icon: <Download sx={{ fontSize: 15 }} />,
              action: handleExportExcel,
            },
            {
              tip: "Refresh",
              icon: <Refresh sx={{ fontSize: 15 }} />,
              action: () => {
                fetchVehicles();
                fetchStats();
              },
            },
          ].map(({ tip, icon, action }) => (
            <Grid item xs={2} sm={1} md={0.75} key={tip}>
              <Tooltip title={tip} arrow>
                <Button
                  fullWidth
                  size="small"
                  onClick={action}
                  sx={{ ...iconBtn, height: 34 }}
                >
                  {icon}
                </Button>
              </Tooltip>
            </Grid>
          ))}
          <Grid
            item
            xs={false}
            sm={false}
            md
            sx={{ display: { xs: "none", md: "block" } }}
          />
          <Grid item xs={6} sm={2} md={1.5}>
            <Box
              sx={{
                height: 34,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.75,
                ...sq,
                bgcolor: T.white,
                color: T.primary,
                border: `1.5px solid ${T.border}`,
                fontWeight: 700,
                fontSize: "0.72rem",
              }}
            >
              <VehicleIcon sx={{ fontSize: 16, color: T.primary }} />
              <Typography
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  color: T.primary,
                  lineHeight: 1,
                }}
              >
                {totalCount} Vehicle{totalCount !== 1 ? "s" : ""}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={6} sm={2} md={1.5}>
            <Button
              fullWidth
              onClick={() => navigate("/dashboard/VehicleFrom")}
              startIcon={<AddIcon sx={{ fontSize: 15 }} />}
              sx={{ ...primaryBtn, height: 34 }}
            >
              Add Vehicle
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Table / Cards */}
      {isMobile ? (
        loading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              py: 10,
              gap: 2,
            }}
          >
            <CircularProgress sx={{ color: T.primary }} size={32} />
            <Typography sx={{ fontSize: "0.74rem", color: T.textSub }}>
              Loading…
            </Typography>
          </Box>
        ) : paginatedVehicles.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              border: `1.5px dashed ${T.border}`,
              bgcolor: T.white,
              ...sq,
            }}
          >
            <VehicleIcon sx={{ fontSize: 44, color: T.border, mb: 1 }} />
            <Typography
              sx={{
                fontWeight: 700,
                color: T.text,
                mb: 0.5,
                fontSize: "0.9rem",
              }}
            >
              No vehicles found
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: T.textSub, mb: 2 }}>
              {searchTerm
                ? "Try a different search term"
                : "Add your first vehicle"}
            </Typography>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => navigate("/dashboard/VehicleFrom")}
              sx={{ ...primaryBtn, px: 2 }}
            >
              Add Vehicle
            </Button>
          </Box>
        ) : (
          <>
            {paginatedVehicles.map((vehicle) => {
              const id = vehicle.vehicle_id || vehicle.id;
              return (
                <MobileVehicleCard
                  key={id}
                  vehicle={vehicle}
                  expanded={expandedCardId === id}
                  onToggle={() => handleCardToggle(id)}
                  onEdit={handleEdit}
                  onDelete={handleDeleteClick}
                  onViewDocuments={handleViewDocuments}
                />
              );
            })}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(_, p) => {
                setPage(p);
                setExpandedCardId(null);
              }}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
                setExpandedCardId(null);
              }}
              sx={paginationSx}
            />
          </>
        )
      ) : (
        <DesktopVehicleTable
          vehicles={paginatedVehicles}
          loading={loading}
          totalCount={totalCount}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, p) => setPage(p)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDeleteClick}
          onViewDocuments={handleViewDocuments}
        />
      )}

      {/* Overlays */}
      <DeletePopover
        anchorEl={deleteAnchorEl}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
      />
      <ViewVehicleDialog
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        vehicle={selectedVehicle}
      />
      <ViewDocumentsDialog
        open={documentsDialogOpen}
        onClose={() => setDocumentsDialogOpen(false)}
        vehicleId={
          selectedVehicleForDocs?.vehicle_id || selectedVehicleForDocs?.id
        }
        vehicleNumber={selectedVehicleForDocs?.vehicle_number}
      />
    </Box>
  );
}
